import { useEffect, useState, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { Hash, Plus, X, MessageSquare, MoreVertical, Pencil, Trash2 } from "lucide-react";
import MessageComposer, { MessageAttachments } from "@/components/MessageComposer";
import MessageActions, { EditedTag } from "@/components/MessageActions";
import { useConfirm } from "@/components/ConfirmModal";

export default function MessagesPage() {
  const { user } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgBody, setMsgBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [unread, setUnread] = useState({}); // {channel_id: count}
  const [menuOpen, setMenuOpen] = useState(null); // channel id whose kebab menu is open
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const endRef = useRef(null);
  const isAdmin = user.role === "admin";

  const loadChannels = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/channels");
      setChannels(data);
      if (!active && data[0]) setActive(data[0]);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };

  const refreshUnread = async () => {
    try {
      const { data } = await api.get("/messages/unread-count");
      setUnread(data.per_channel || {});
    } catch { /* ignore */ }
  };

  useEffect(() => { loadChannels(); refreshUnread(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // When the active channel changes, load messages AND mark it read on the server.
  useEffect(() => {
    if (!active) return;
    api.get(`/channels/${active.id}/messages`).then(({ data }) => setMessages(data)).catch(() => setMessages([]));
    api.post(`/channels/${active.id}/mark-read`).then(refreshUnread).catch(() => {});
  }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // After a fresh message is sent, refresh unread so badges adjust quickly
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (body, attachments) => {
    if (!active) return;
    try {
      const { data } = await api.post(`/channels/${active.id}/messages`, { Body: body, Attachments: attachments });
      setMessages((m) => [...m, data]);
      // mark this channel as read so the sidebar count doesn't include our own message
      api.post(`/channels/${active.id}/mark-read`).then(refreshUnread).catch(() => {});
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const renameChannel = async (ch) => {
    const name = renameDraft.trim();
    if (!name || name === ch.Channel_Name) { setRenamingId(null); return; }
    try {
      const { data } = await api.patch(`/channels/${ch.id}`, { Channel_Name: name });
      setChannels((cs) => cs.map((c) => (c.id === ch.id ? data : c)));
      if (active?.id === ch.id) setActive(data);
      toast.success("Channel renamed.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setRenamingId(null); }
  };

  const deleteChannel = async (ch) => {
    const ok = await confirm({
      title: `Delete #${ch.Channel_Name}?`,
      message: "All messages and notifications in this channel will be permanently removed. System channels will respawn on next restart.",
      confirmLabel: "Delete channel",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/channels/${ch.id}`);
      setChannels((cs) => cs.filter((c) => c.id !== ch.id));
      if (active?.id === ch.id) setActive(null);
      toast.success("Channel deleted.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setMenuOpen(null); }
  };

  return (
    <div className="space-y-6 h-full flex flex-col">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="es-overline text-brand-600 mb-2">// internal comms</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Messages</h1>
        </div>
        <button data-testid="channel-new-button" onClick={() => setShowNew(true)} className="es-btn-primary">
          <Plus className="w-4 h-4" /> New channel
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 flex-1">
        {/* Channels list */}
        <aside className="es-card p-2 lg:col-span-1">
          <div className="es-overline px-3 py-2">{channels.length} channels</div>
          {loading && <div className="px-3 py-4 text-xs font-mono text-zinc-500">Loading…</div>}
          {!loading && channels.length === 0 && <div className="px-3 py-4 text-xs font-mono text-zinc-500">No channels yet.</div>}
          {channels.map((ch) => {
            const n = unread[ch.id] || 0;
            const isActive = active?.id === ch.id;
            const isRenaming = renamingId === ch.id;
            return (
              <div key={ch.id} className="relative group">
                {isRenaming ? (
                  <div className="px-3 py-2 flex items-center gap-2">
                    <Hash className="w-4 h-4 flex-shrink-0 text-zinc-400" />
                    <input
                      data-testid={`channel-rename-input-${ch.id}`}
                      autoFocus
                      value={renameDraft}
                      onChange={(e) => setRenameDraft(e.target.value)}
                      onBlur={() => renameChannel(ch)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
                        if (e.key === "Escape") { setRenamingId(null); }
                      }}
                      className="flex-1 text-sm bg-white border border-brand-500 rounded px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-brand-200"
                    />
                  </div>
                ) : (
                  <button
                    data-testid={`channel-${ch.id}`}
                    onClick={() => setActive(ch)}
                    className={[
                      "w-full text-left px-3 py-2 rounded-sm flex items-center gap-2 text-sm transition-colors",
                      isAdmin ? "pr-9" : "",
                      isActive ? "bg-brand-100 text-brand-900 font-semibold" :
                        (n > 0 ? "font-bold text-zinc-900 hover:bg-zinc-100" : "text-zinc-600 hover:bg-zinc-100"),
                    ].join(" ")}
                  >
                    <Hash className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{ch.Channel_Name}</span>
                    {n > 0 && !isActive && (
                      <span
                        data-testid={`channel-unread-${ch.id}`}
                        className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-mono font-bold leading-none"
                      >
                        {n > 99 ? "99+" : n}
                      </span>
                    )}
                    {(n === 0 || isActive) && (
                      <span className="es-badge es-badge-neutral ml-auto text-[10px]">{ch.Channel_Type}</span>
                    )}
                  </button>
                )}
                {isAdmin && !isRenaming && (
                  <button
                    data-testid={`channel-menu-${ch.id}`}
                    onClick={(e) => { e.stopPropagation(); setMenuOpen((m) => (m === ch.id ? null : ch.id)); }}
                    className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-zinc-500 bg-white/70 hover:text-zinc-900 hover:bg-white border border-transparent hover:border-zinc-200 shadow-sm focus:opacity-100"
                    aria-label="Channel actions"
                    title="Channel actions (rename, delete)"
                  ><MoreVertical className="w-4 h-4" /></button>
                )}
                {isAdmin && menuOpen === ch.id && (
                  <div className="absolute right-0 top-full mt-1 z-30 bg-white shadow-lg rounded-lg border border-zinc-200 py-1 w-36" onMouseLeave={() => setMenuOpen(null)}>
                    <button
                      data-testid={`channel-rename-${ch.id}`}
                      onClick={() => { setRenamingId(ch.id); setRenameDraft(ch.Channel_Name); setMenuOpen(null); }}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-zinc-50 inline-flex items-center gap-2"
                    ><Pencil className="w-3.5 h-3.5" /> Rename</button>
                    <button
                      data-testid={`channel-delete-${ch.id}`}
                      onClick={() => deleteChannel(ch)}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-red-50 text-red-600 inline-flex items-center gap-2"
                    ><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                  </div>
                )}
              </div>
            );
          })}
        </aside>

        {/* Active channel */}
        <section className="es-card lg:col-span-3 flex flex-col min-h-[500px]">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-zinc-500 font-mono text-sm">
              <div className="text-center">
                <MessageSquare className="w-8 h-8 mx-auto mb-3 text-zinc-300" />
                Select a channel to start.
              </div>
            </div>
          ) : (
            <>
              <header className="border-b border-zinc-200 px-5 py-3 flex items-center gap-2">
                <Hash className="w-4 h-4 text-zinc-500" />
                <h2 className="font-display font-bold">{active.Channel_Name}</h2>
                <span className="es-badge es-badge-neutral ml-2">{active.Channel_Type}</span>
              </header>
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="messages-list">
                {messages.length === 0 && <div className="text-xs font-mono text-zinc-500">No messages — be the first to post.</div>}
                {messages.map((m) => (
                  <div key={m.id} className="flex gap-3 group" data-testid={`msg-${m.id}`}>
                    <div className="w-9 h-9 bg-zinc-200 flex items-center justify-center font-semibold text-sm flex-shrink-0">
                      {m.Sender_Name?.[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="font-semibold text-sm">{m.Sender_Name}</span>
                        <span className="text-xs font-mono text-zinc-400">{new Date(m.created_date).toLocaleString()}</span>
                        <EditedTag message={m} />
                        <MessageActions
                          message={m}
                          user={user}
                          onUpdated={(fresh) => setMessages((ms) => ms.map((x) => x.id === fresh.id ? fresh : x))}
                          onDeleted={(id) => setMessages((ms) => ms.filter((x) => x.id !== id))}
                          onRestored={(msg) => setMessages((ms) => {
                            const exists = ms.some((x) => x.id === msg.id);
                            const next = exists ? ms : [...ms, msg];
                            // keep timeline sort stable on created_date
                            return next.slice().sort((a, b) => (a.created_date || "").localeCompare(b.created_date || ""));
                          })}
                        />
                      </div>
                      {m.Body && <div className="text-sm text-zinc-800 mt-1 whitespace-pre-wrap">{m.Body}</div>}
                      <MessageAttachments attachments={m.Attachments} />
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="border-t border-zinc-200 p-3">
                <MessageComposer placeholder={`Message #${active.Channel_Name}`} onSend={send} />
              </div>
            </>
          )}
        </section>
      </div>

      {showNew && <NewChannelModal onClose={() => setShowNew(false)} onCreated={(c) => { setChannels((cs) => [c, ...cs]); setActive(c); setShowNew(false); }} />}
    </div>
  );
}

function NewChannelModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("general");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    try { const { data } = await api.post("/channels", { Channel_Name: name, Channel_Type: type, Member_IDs: [] }); onCreated(data); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-zinc-900/60 z-40 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm border border-zinc-300 w-full max-w-md">
        <div className="flex items-center justify-between border-b border-zinc-200 px-6 py-4">
          <h3 className="font-display text-lg font-bold">New Channel</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div>
            <label className="es-label">Channel name *</label>
            <input required value={name} onChange={(e) => setName(e.target.value)} className="es-input" />
          </div>
          <div>
            <label className="es-label">Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)} className="es-select w-full">
              <option>general</option>
              <option>order</option>
              <option>company</option>
            </select>
          </div>
          {err && <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-sm">{err}</div>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="es-btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="es-btn-primary">{saving ? "Saving…" : "Create"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
